/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useMemo, useState } from 'react';
import {
  ButtonBase,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SortIcon from '@material-ui/icons/Sort';
import { KgDirtree, RepoGraphEntry, RepoHealthStatus, shaMatches } from './types';
import { formatRelativeTime } from './relativeTime';
import { KgNodesIcon } from './KgNodesIcon';
import { RepoHealth, repoHealthOf } from './repoHealth';

// Master list for the knowledge-graph workbench: every graph in the store,
// grouped by GitHub org (repos are org/name slugs — the store layout is
// repo/<org>/<name>/), with the pinned Global entry on top. Built for a
// fleet of 200+ repos: a name filter, collapsible org sections, and per-row
// stats that double as the "cached, opens instantly" indicator.

const useStyles = makeStyles(theme => ({
  root: {
    width: 280,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(1.5),
    overflowY: 'auto',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingRight: 0,
      paddingBottom: theme.spacing(1),
      marginBottom: theme.spacing(1),
      maxHeight: 280,
    },
  },
  heading: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: theme.spacing(1, 2, 0.5),
  },
  filter: {
    padding: theme.spacing(0.5, 2, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  sortMenuArrow: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
    display: 'inline-flex',
    color: theme.palette.text.secondary,
  },
  // Org sections are the rail's top hierarchy level: uppercase banded
  // headings that stick while their repos scroll under them — unmistakably
  // structure, never another graph row.
  orgHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    lineHeight: 1.4,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    background: theme.palette.background.paper,
    boxShadow: `inset 0 0 0 100vmax ${theme.palette.action.hover}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    cursor: 'pointer',
  },
  orgHeaderSelected: {
    boxShadow: `inset 0 0 0 100vmax ${theme.palette.action.selected}`,
    borderTop: `1px solid ${theme.palette.primary.main}`,
  },
  orgCount: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: 12,
    textTransform: 'none',
    letterSpacing: 0,
    marginLeft: 8,
  },
  // The expanded directory tree is its repo's code space: a guide rail
  // contains it so repos and their trees never blur together.
  treeRegion: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(2.5),
    marginBottom: theme.spacing(0.5),
  },
  item: {
    borderRadius: 4,
  },
  meta: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  globalIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1.25),
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  orgIcon: {
    fontSize: 13,
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
  },
  updatedLine: {
    display: 'block',
    fontSize: 11,
    opacity: 0.75,
  },
  // Fleet-health dot (PLAT-1702): outcome + age as one glanceable color;
  // the verdict text lives in the tooltip and the aria-label.
  healthDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    verticalAlign: 'baseline',
  },
  healthOk: { backgroundColor: theme.palette.success.main },
  healthStale: { backgroundColor: theme.palette.warning.main },
  healthAttention: { backgroundColor: theme.palette.warning.dark },
  healthFailed: { backgroundColor: theme.palette.error.main },
  healthNone: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.text.disabled}`,
  },
}));

const HEALTH_DOT_CLASS: Record<RepoHealth['tone'], keyof ReturnType<typeof useStyles>> = {
  ok: 'healthOk',
  stale: 'healthStale',
  attention: 'healthAttention',
  failed: 'healthFailed',
  none: 'healthNone',
};

// Pure grouping over org/name slugs: org -> sorted slugs; a slug without an
// org segment lands in 'other', which always sorts last. Exported for tests
// and for keeping the fleet overview's card order consistent with the panel.
export const groupReposByOrg = (
  repos: string[],
): { org: string; repos: string[] }[] => {
  const buckets = new Map<string, string[]>();
  for (const slug of repos) {
    const org = slug.includes('/') ? slug.split('/')[0] : 'other';
    const list = buckets.get(org) ?? [];
    list.push(slug);
    buckets.set(org, list);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => {
      if (a === 'other') return 1;
      if (b === 'other') return -1;
      return a.localeCompare(b);
    })
    .map(([org, list]) => ({ org, repos: list.sort((a, b) => a.localeCompare(b)) }));
};

// Rail ordering: the org banding is the hierarchy, so sort applies within
// each section. Direction defaults follow intent — names read A->Z, "last
// updated" answers "what changed?" (newest first), size ranks biggest first.
// Newest-first is the default: the rail doubles as a live feed, fresh
// builds lining up on top as the 60s pointer sweep lands them (that sweep
// is visibility-gated but never pauses; only the global-graph poll does).
type RepoSortKey = 'name' | 'updated' | 'size';
export type RepoSort = { key: RepoSortKey; dir: 'asc' | 'desc' };
const SORT_STORAGE_KEY = 'knowledgeGraph.repoSort';
const SORT_DEFAULT_DIR: Record<RepoSortKey, 'asc' | 'desc'> = {
  name: 'asc',
  updated: 'desc',
  size: 'desc',
};
const sortDescription = (s: RepoSort): string => {
  if (s.key === 'name') return `name, ${s.dir === 'asc' ? 'A to Z' : 'Z to A'}`;
  if (s.key === 'updated')
    return `last updated, ${s.dir === 'desc' ? 'newest' : 'oldest'} first`;
  return `size, ${s.dir === 'desc' ? 'largest' : 'smallest'} first`;
};
// Unknown values (no summary cached yet, no store timestamp) sort last in
// either direction — a row must never win a ranking by having no data. The
// slug tie-break is deliberately un-flipped so equal rows stay stable.
export const compareRepoRows =
  (dir: 'asc' | 'desc', value: (slug: string) => string | number | null) =>
  (a: string, b: string): number => {
    const va = value(a);
    const vb = value(b);
    if (va === null && vb === null) return a.localeCompare(b);
    if (va === null) return 1;
    if (vb === null) return -1;
    const flip = dir === 'desc' ? -1 : 1;
    const cmp =
      typeof va === 'number'
        ? va - (vb as number)
        : String(va).localeCompare(String(vb));
    return cmp === 0 ? a.localeCompare(b) : cmp * flip;
  };

const readStoredSort = (): RepoSort => {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as RepoSort;
      if (
        ['name', 'updated', 'size'].includes(parsed.key) &&
        ['asc', 'desc'].includes(parsed.dir)
      ) {
        return parsed;
      }
    }
  } catch {
    // fall through to the default
  }
  return { key: 'updated', dir: 'desc' };
};

// "m42-platform/svc-test" -> "svc-test"; org context lives in the section
// header, so rows carry just the name.
export const repoDisplayName = (slug: string): string =>
  slug.includes('/') ? slug.slice(slug.indexOf('/') + 1) : slug;

export const GraphListPanel = (props: {
  repos: string[]; // org/name slugs
  listError?: string | null;
  entries: Record<string, RepoGraphEntry>;
  latestShas?: Record<string, string>;
  latestUpdated?: Record<string, string>;
  healthStatuses?: Record<string, RepoHealthStatus>;
  selected: string; // 'global' | org/name slug
  onSelect: (slug: string) => void;
  // Org bands are selectable views: clicking one opens the org-scoped fleet
  // overview; the chevron alone collapses the section.
  onSelectOrg?: (org: string) => void;
  // Giant repos (summary.dirtree) expand into their directory tree so the
  // rail shows how many symbols sit below each box before opening it.
  loadDirtree?: (slug: string) => Promise<KgDirtree>;
  onOpenDir?: (slug: string, path: string) => void;
  selectedPath?: string;
}) => {
  const classes = useStyles();
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Expanded dir keys: `${slug}` for the repo root, `${slug}//${path}` below.
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  const [trees, setTrees] = useState<Record<string, KgDirtree | 'loading' | 'error'>>({});

  const toggleDir = (key: string) =>
    setExpandedDirs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleRepoTree = async (slug: string) => {
    toggleDir(slug);
    if (trees[slug] || !props.loadDirtree) return;
    setTrees(prev => ({ ...prev, [slug]: 'loading' }));
    try {
      const tree = await props.loadDirtree(slug);
      setTrees(prev => ({ ...prev, [slug]: tree }));
    } catch {
      setTrees(prev => ({ ...prev, [slug]: 'error' }));
    }
  };

  const [sort, setSort] = useState<RepoSort>(readStoredSort);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const applySort = (key: RepoSortKey) => {
    setSort(prev => {
      const next: RepoSort =
        prev.key === key
          ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
          : { key, dir: SORT_DEFAULT_DIR[key] };
      try {
        localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // private mode etc. — the choice just doesn't persist
      }
      return next;
    });
    setSortAnchor(null);
  };

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const visible = q
      ? props.repos.filter(r => r.toLowerCase().includes(q))
      : props.repos;
    const grouped = groupReposByOrg(visible);
    if (sort.key === 'name' && sort.dir === 'asc') return grouped;
    const value = (slug: string): string | number | null => {
      if (sort.key === 'name') return repoDisplayName(slug).toLowerCase();
      if (sort.key === 'updated') return props.latestUpdated?.[slug] ?? null;
      return props.entries[slug]?.stats?.symbols ?? null;
    };
    const compare = compareRepoRows(sort.dir, value);
    return grouped.map(g => ({ ...g, repos: [...g.repos].sort(compare) }));
  }, [props.repos, props.entries, props.latestUpdated, filter, sort]);
  const filterActive = filter.trim().length > 0;

  const toggleOrg = (org: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(org)) next.delete(org);
      else next.add(org);
      return next;
    });

  const metaFor = (name: string) => {
    const e = props.entries[name];
    if (e?.stats) {
      const sha = e.stats.sha ? ` · @${e.stats.sha.slice(0, 7)}` : '';
      const latest = props.latestShas?.[name];
      const stale = latest && e.stats.sha && !shaMatches(latest, e.stats.sha);
      return `${e.stats.symbols} nodes · ${e.stats.edges} edges${sha}${stale ? ' · updating…' : ''}`;
    }
    if (e?.status === 'error') return 'not cached — retries on open';
    return 'caching…';
  };

  const dirRows = (slug: string, tree: KgDirtree, parent: string, depth: number): React.ReactNode[] =>
    tree.dirs
      .filter(d => d.parent === parent)
      .sort((a, b) => b.symbols - a.symbols)
      .map(d => {
        const key = `${slug}//${d.path}`;
        // Truth from the tree, not the declared count: children below a
        // shard frontier are pruned from dirtree, so a sharded dir's count
        // can promise rows that do not exist.
        const expandable = tree.dirs.some(x => x.parent === d.path);
        const open = expandedDirs.has(key);
        const isCurrent = props.selected === slug && props.selectedPath === d.path;
        return (
          <React.Fragment key={key}>
            <ListItem
              button
              dense
              className={classes.item}
              style={{ paddingLeft: 10 + depth * 14 }}
              selected={isCurrent}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => props.onOpenDir?.(slug, d.path)}
            >
              <ListItemText
                primary={d.name}
                primaryTypographyProps={{ style: { fontSize: 13 } }}
                secondary={`${d.symbols.toLocaleString()} symbols${
                  d.shard ? '' : ` · ${d.children} dirs`
                }`}
                secondaryTypographyProps={{ className: classes.meta }}
              />
              {expandable && (
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`${open ? 'collapse' : 'expand'} ${d.path}`}
                  aria-expanded={open}
                  onClick={e => {
                    e.stopPropagation();
                    toggleDir(key);
                  }}
                >
                  {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
            </ListItem>
            {expandable && open && dirRows(slug, tree, d.path, depth + 1)}
          </React.Fragment>
        );
      });

  const repoRow = (slug: string) => {
    const isGiant = !!props.entries[slug]?.summary?.dirtree && !!props.loadDirtree;
    const open = expandedDirs.has(slug);
    const tree = trees[slug];
    return (
      <React.Fragment key={slug}>
        <ListItem
          button
          className={classes.item}
          selected={props.selected === slug && !props.selectedPath}
          aria-current={props.selected === slug ? 'true' : undefined}
          onClick={() => props.onSelect(slug)}
        >
          <ListItemText
            primary={
              <>
                {(() => {
                  const health: RepoHealth = repoHealthOf(
                    props.healthStatuses?.[slug] ?? null,
                    props.latestUpdated?.[slug] ?? null,
                    Date.now(),
                  );
                  return (
                    <Tooltip title={health.label}>
                      <span
                        role="img"
                        aria-label={`graph health: ${health.label}`}
                        className={`${classes.healthDot} ${classes[HEALTH_DOT_CLASS[health.tone]]}`}
                      />
                    </Tooltip>
                  );
                })()}
                {repoDisplayName(slug)}
              </>
            }
            primaryTypographyProps={{
              style: { fontWeight: props.selected === slug ? 600 : 400 },
            }}
            secondary={
              <>
                {metaFor(slug)}
                {props.latestUpdated?.[slug] &&
                  formatRelativeTime(props.latestUpdated[slug]) && (
                    <span
                      className={classes.updatedLine}
                      title={new Date(
                        props.latestUpdated[slug],
                      ).toLocaleString()}
                    >
                      updated {formatRelativeTime(props.latestUpdated[slug])}
                    </span>
                  )}
              </>
            }
            secondaryTypographyProps={{ className: classes.meta }}
          />
          {isGiant && (
            <IconButton
              size="small"
              edge="end"
              aria-label={`${open ? 'collapse' : 'expand'} directories of ${slug}`}
              aria-expanded={open}
              onClick={e => {
                e.stopPropagation();
                toggleRepoTree(slug);
              }}
            >
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </ListItem>
        {isGiant && open && tree === 'loading' && (
          <Typography component="p" className={classes.meta} style={{ paddingLeft: 30 }}>
            loading directories…
          </Typography>
        )}
        {isGiant && open && tree === 'error' && (
          <Typography role="alert" component="p" className={classes.meta} style={{ paddingLeft: 30 }}>
            couldn't load the directory tree
          </Typography>
        )}
        {isGiant && open && tree && typeof tree === 'object' && (
          <div className={classes.treeRegion} role="group" aria-label={`directories of ${slug}`}>
            {dirRows(slug, tree, '', 0)}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <nav className={classes.root} aria-label="graphs in the store">
      <Typography className={classes.heading} component="h2">
        Graphs
      </Typography>
      <div className={classes.filter}>
        <Tooltip title={`Sort: ${sortDescription(sort)}`}>
          <IconButton
            size="small"
            aria-label={`sort repos — currently by ${sortDescription(sort)}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(sortAnchor)}
            onClick={e => setSortAnchor(e.currentTarget)}
          >
            <SortIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={sortAnchor}
          open={Boolean(sortAnchor)}
          onClose={() => setSortAnchor(null)}
        >
          {(
            [
              ['name', 'Name'],
              ['updated', 'Last updated'],
              ['size', 'Size (nodes)'],
            ] as [RepoSortKey, string][]
          ).map(([key, label]) => (
            <MenuItem
              key={key}
              dense
              selected={sort.key === key}
              aria-label={
                sort.key === key ? `${label} — ${sortDescription(sort)}` : undefined
              }
              onClick={() => applySort(key)}
            >
              {label}
              {sort.key === key && (
                <span className={classes.sortMenuArrow}>
                  {sort.dir === 'asc' ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  )}
                </span>
              )}
            </MenuItem>
          ))}
        </Menu>
        <TextField
          id="kg-repo-filter"
          size="small"
          variant="outlined"
          fullWidth
          label="Filter repos"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear repo filter"
                  onClick={() => setFilter('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </div>
      {props.listError && props.repos.length > 0 && (
        <Typography role="alert" component="p" className={classes.meta} style={{ padding: '0 16px 4px' }}>
          {props.listError}
        </Typography>
      )}
      <List dense disablePadding>
        <ListItem
          button
          className={classes.item}
          selected={props.selected === 'global'}
          aria-current={props.selected === 'global' ? 'true' : undefined}
          onClick={() => props.onSelect('global')}
        >
          <KgNodesIcon variant="duo" className={classes.globalIcon} />
          <ListItemText
            primary="Global"
            primaryTypographyProps={{
              style: { fontWeight: props.selected === 'global' ? 600 : 400 },
            }}
            secondary={`fleet overview · ${props.repos.length} repo${props.repos.length === 1 ? '' : 's'}`}
            secondaryTypographyProps={{ className: classes.meta }}
          />
        </ListItem>
        {groups.map(({ org, repos }) => {
          // An active filter overrides collapse — hits must be visible.
          const open = filterActive || !collapsed.has(org);
          return (
            <React.Fragment key={org}>
              <ListSubheader
                component="div"
                disableSticky
                disableGutters
                className={`${classes.orgHeader}${
                  props.selected === `org:${org}` ? ` ${classes.orgHeaderSelected}` : ''
                }`}
              >
                <ButtonBase
                  style={{
                    flexGrow: 1,
                    justifyContent: 'flex-start',
                    font: 'inherit',
                    letterSpacing: 'inherit',
                    textTransform: 'inherit',
                    color: 'inherit',
                  }}
                  aria-current={props.selected === `org:${org}` ? 'true' : undefined}
                  onClick={() =>
                    props.onSelectOrg ? props.onSelectOrg(org) : toggleOrg(org)
                  }
                >
                  <KgNodesIcon variant="duo" className={classes.orgIcon} />
                  {org}
                  <span className={classes.orgCount}>· {repos.length}</span>
                </ButtonBase>
                <IconButton
                  size="small"
                  aria-label={`${open ? 'collapse' : 'expand'} ${org} section`}
                  aria-expanded={open}
                  onClick={() => toggleOrg(org)}
                >
                  {open ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </ListSubheader>
              <Collapse in={open} timeout="auto" unmountOnExit>
                {repos.map(repoRow)}
              </Collapse>
            </React.Fragment>
          );
        })}
        {!props.repos.length && (
          <ListItem className={classes.item}>
            <ListItemText
              secondary={
                props.listError
                  ? `Couldn't list the graph store — ${props.listError}`
                  : 'No Level-1 graphs in the store yet.'
              }
              secondaryTypographyProps={{
                className: classes.meta,
                ...(props.listError ? { role: 'alert' } : {}),
              }}
            />
          </ListItem>
        )}
        {props.repos.length > 0 && filterActive && groups.length === 0 && (
          <ListItem className={classes.item}>
            <ListItemText
              secondary={`No repos match “${filter.trim()}”.`}
              secondaryTypographyProps={{ className: classes.meta }}
            />
          </ListItem>
        )}
      </List>
    </nav>
  );
};
